import Divider from '@material-ui/core/Divider';
import { Theme } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import ErrorIcon from '@material-ui/icons/Error';
import WarningIcon from '@material-ui/icons/WarningRounded';
import React from 'react';
import { Observe } from 'rewire-core';
import { DefaultActionRenderer, FormView, WithStyle } from 'rewire-ui';
import DraggableResizableDialog from '../ui/DraggableResizableDialog';
import Grid from '../ui/Grid';
import { withStyles } from '../utility/styles';
import infoPanelShiftSchedulingViewModel from './InfoPanelShiftSchedulingViewModel';
import responsesLogViewModel from './ResponsesLogViewModel';
import schedulingCandidatePhoneDialog from './SchedulingCandidatePhoneDialogModel';

const styles = (_theme: Theme) => ({
  errorIcon: {
    color: _theme.palette.error.main,
  },
  warningIcon: {
    color: '#cc8100',
  },
  draggableResizableDialogRoot: {
    '& tr.matches.error': {
      fontStyle: 'italic',
      color: _theme.palette.error.main,
    },
    '& tr.matches.warning': {
      fontStyle: 'italic',
      color: '#cc8100',
    },
  },
});

interface ISchedulingCandidatePhoneDialogProps {}

type SchedulingCandidatePhoneDialogProps = WithStyle<ReturnType<typeof styles>, ISchedulingCandidatePhoneDialogProps>;

class SchedulingCandidatePhoneDialog extends React.PureComponent<SchedulingCandidatePhoneDialogProps> {
  constructor(props: SchedulingCandidatePhoneDialogProps) {
    super(props);
  }

  componentWillUnmount() {
    schedulingCandidatePhoneDialog.reset();
  }

  renderShiftGrid = React.memo((): JSX.Element => {
    const gridFontSizes = {
      header: '0.75em',
      body: '0.75em',
      groupRow: '1em',
      toggleMenu: '0.8em',
    };

    const gridColors = {
      cellSelectedBackground: '#D0DEE8',
      cellOutline: 'none',
    };

    React.useEffect(() => {
      infoPanelShiftSchedulingViewModel.shiftGrid.canSelectCellFn = () => false;
      return () => {
        infoPanelShiftSchedulingViewModel.shiftGrid.canSelectCellFn = () => true;
      };
    }, []);

    return (
      <Observe
        render={() =>
          (infoPanelShiftSchedulingViewModel.shiftGrid?.id && (
            <Grid
              key={infoPanelShiftSchedulingViewModel.shiftGrid.id}
              grid={infoPanelShiftSchedulingViewModel.shiftGrid}
              gridFontSizes={gridFontSizes}
              gridColors={gridColors}
            />
          )) ||
          null
        }
      />
    );
  });

  renderResponseLog = React.memo((): JSX.Element => {
    const gridFontSizes = {
      header: '0.75em',
      body: '0.75em',
      groupRow: '1em',
      toggleMenu: '0.8em',
    };

    const gridColors = {
      // cellSelectedBackground: '#D0DEE8',
      // cellOutline: 'none',
    };

    return (
      <Observe
        render={() =>
          (responsesLogViewModel.grid?.id && (
            <Grid
              key={responsesLogViewModel.grid.id}
              grid={responsesLogViewModel.grid}
              gridFontSizes={gridFontSizes}
              gridColors={gridColors}
            />
          )) ||
          null
        }
      />
    );
  });

  renderDialogContents = React.memo((): JSX.Element => {
    return (
      <Observe
        render={() => {
          let action = 'schedule employee';
          const response = schedulingCandidatePhoneDialog.form.field.response.value;
          if (response === 'no' || response === 'unavailable') {
            action = 'record response';
          }
          return (
            <FormView form={schedulingCandidatePhoneDialog.form} onSubmit={schedulingCandidatePhoneDialog.actionFn(action)}>
              <div className="content">
                <schedulingCandidatePhoneDialog.form.field.work.Editor className="span3" />
                <schedulingCandidatePhoneDialog.form.field.mobile.Editor className="span3" />
                <schedulingCandidatePhoneDialog.form.field.home.Editor className="span3" />
              </div>
              <br />
              <div className="content">
                <schedulingCandidatePhoneDialog.form.field.reason.Editor
                  className="span3"
                  disabled={action !== 'schedule employee'}
                />
                <schedulingCandidatePhoneDialog.form.field.response.Editor className="span3" />
                <schedulingCandidatePhoneDialog.form.field.comment.Editor className="span3" />
              </div>
            </FormView>
          );
        }}
      />
    );
  });

  renderButtons = (props: any) => {
    return (
      <Observe
        render={() => {
          const response = schedulingCandidatePhoneDialog.form.field.response.value;
          let action = 'schedule employee';
          switch (response) {
            case 'unavailable':
            case 'no':
              action = 'record response';
              break;
            default:
              action = 'schedule employee';
          }
          return props.label === action || props.label === 'cancel' ? <DefaultActionRenderer {...props} /> : null;
        }}
      />
    );
  };

  render() {
    const { classes } = this.props;
    return (
      <Observe
        render={() => {
          const responseWithHighestLevel = responsesLogViewModel.responseWithHighestLevel;
          const warning = !responseWithHighestLevel ? null : responseWithHighestLevel.level === 'error' ? (
            <div title={responseWithHighestLevel.message}>
              <ErrorIcon className={classes.errorIcon} />
            </div>
          ) : responseWithHighestLevel.level === 'warning' ? (
            <div title={responseWithHighestLevel.message}>
              <WarningIcon className={classes.warningIcon} />
            </div>
          ) : null;
          return (
            <DraggableResizableDialog
              defaultWidth="md"
              DialogProps={{
                dialog: schedulingCandidatePhoneDialog,
                ButtonRenderer: this.renderButtons,
              }}
              DraggableResizableBoxProps={{ minWidth: '250px', minHeight: '213px' }}
            >
              <div
                className={classes.draggableResizableDialogRoot}
                style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '0 16px' }}
              >
                <Typography variant="h6">Vacant Shift Details</Typography>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Observe
                    render={() => <Typography>{infoPanelShiftSchedulingViewModel.occupation?.name ?? 'No Occupation'}</Typography>}
                  />
                  <Observe
                    render={() => (
                      <Typography>
                        {infoPanelShiftSchedulingViewModel.toDisplayDateString(infoPanelShiftSchedulingViewModel.earnedDate)}
                      </Typography>
                    )}
                  />
                </div>
                <this.renderShiftGrid />
                <Typography variant="h6">Responses</Typography>
                <this.renderResponseLog />
                <Divider />

                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="h6">
                    {schedulingCandidatePhoneDialog.employee?.name ?? 'No Employee'} - Phone Details
                  </Typography>
                  {warning}
                </div>
                <this.renderDialogContents />
              </div>
            </DraggableResizableDialog>
          );
        }}
      />
    );
  }
}

export default withStyles<ISchedulingCandidatePhoneDialogProps>(styles, SchedulingCandidatePhoneDialog);
